import React, { useState } from "react";
import { Box, InputAdornment, Typography } from "@mui/material";
import {
  StaticDatePicker,
  PickersDay,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { FormattedMessage } from "react-intl";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { InputField } from "../InputField";
import Dialog from "../Dialog";
import Button from "../Button";

import { IButtonVariant } from "@/models/button";

interface IProps {
  selectedDates: string[];
  setSelectedDates: (dates: string[]) => void;
  disabled: boolean;
}

const DatePickerMultipleSelection: React.FC<IProps> = (props) => {
  const { selectedDates, setSelectedDates, disabled } = props;
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDateChange = (date) => {
    const dateStr = dayjs(date).format("ddd, DD MMM YYYY");
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter((d) => d !== dateStr));
    } else {
      setSelectedDates([...selectedDates, dateStr]);
    }
  };

  const renderDay = (date, _, pickersDayProps) => {
    const dateStr = dayjs(date).format("ddd, DD MMM YYYY");

    const isSelected = selectedDates.includes(dateStr);
    return (
      <PickersDay
        {...pickersDayProps}
        sx={{
          backgroundColor: isSelected ? "primary.main" : "transparent",
          color: isSelected ? "white" : "inherit",
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: isSelected ? "primary.dark" : "rgba(0,0,0,0.04)",
          },
        }}
        onClick={() => handleDateChange(date)}
      />
    );
  };

  return (
    <>
      <InputField
        label="Select Date"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography
                component={"p"}
                sx={{
                  color: disabled ? "var(--gray-400)" : "var(--orange-500)",
                  fontSize: "1.4rem",
                  fontWeight: 500,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
                onClick={(e) =>
                  disabled
                    ? null
                    : setAnchorEl(anchorEl ? null : e.currentTarget)
                }
              >
                <FormattedMessage id="select" defaultMessage="Select" />
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiInputBase-root.Mui-disabled": {
            backgroundColor: "var(--blue-gray-100)",
          },
        }}
        disabled={disabled}
        fullWidth
      />
      <Dialog isOpen={Boolean(anchorEl)} closeHandler={() => setAnchorEl(null)}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StaticDatePicker
            minDate={dayjs()}
            displayStaticWrapperAs="desktop"
            value={null}
            onChange={() => {}}
            slots={{
              day: (props) => renderDay(props.day, selectedDates, props),
            }}
          />
        </LocalizationProvider>
        <Box sx={{ p: 1, display: "flex", gap: 1 }}>
          <Button
            variant={IButtonVariant.WHITE}
            btnTrigger={() => setSelectedDates([])}
          >
            Clear
          </Button>
          <Button
            variant={IButtonVariant.ORANGE}
            btnTrigger={() => setAnchorEl(null)}
          >
            Done
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default DatePickerMultipleSelection;
